/**
 * ImageProcessor - Static utility class for processing images using Jimp and Canvas API
 */

import { Jimp } from "jimp";
import type { Settings } from "./types";

export class ImageProcessor {
    /**
     * Process an image file according to the provided settings
     * @param file The image file to process
     * @param settings Processing settings
     * @param onProgress Optional callback for progress updates (0-100)
     * @returns Processed image as a Blob
     */
    static async processImage(
        file: File,
        settings: Settings,
        onProgress?: (progress: number) => void
    ): Promise<Blob> {
        try {
            // Report initial progress
            if (onProgress) onProgress(10);

            // SVG, WebP files or WebP output require Canvas API
            if (file.type === "image/svg+xml" || file.type === "image/webp" || settings.outputFormat === "webp") {
                return await ImageProcessor.processViaCanvas(file, settings, onProgress);
            }

            // Read the file as an ArrayBuffer
            const arrayBuffer = await file.arrayBuffer();
            if (onProgress) onProgress(20);

            // Load the image with Jimp
            const image = await Jimp.read(arrayBuffer);
            if (onProgress) onProgress(40);

            // Apply black and white conversion if requested
            if (settings.convertToBlackAndWhite) {
                image.greyscale();
            }
            if (onProgress) onProgress(50);

            // Apply resize if max dimensions are specified
            if (settings.maxWidth !== null || settings.maxHeight !== null) {
                // Use scaleToFit to maintain aspect ratio
                if (settings.maxWidth && settings.maxHeight) {
                    image.scaleToFit({ w: settings.maxWidth, h: settings.maxHeight });
                } else if (settings.maxWidth) {
                    image.resize({ w: settings.maxWidth });
                } else if (settings.maxHeight) {
                    image.resize({ h: settings.maxHeight });
                }
            }
            if (onProgress) onProgress(70);

            // Get the MIME type for the output format
            const mimeType = ImageProcessor.getMimeType(settings.outputFormat);

            if (onProgress) onProgress(80);

            // Convert to buffer with quality setting for JPEG
            const buffer = await image.getBuffer(mimeType as any, { 
                quality: settings.outputFormat === "jpeg" ? settings.quality : undefined 
            });
            if (onProgress) onProgress(90);

            // Convert buffer to Blob
            const blob = new Blob([buffer as any], { type: mimeType });
            if (onProgress) onProgress(100);

            return blob;
        } catch (error) {
            throw new Error(`Failed to process image: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
    }

    /**
     * Process image via Canvas API (used for WebP output)
     */
    private static async processViaCanvas(
        file: File,
        settings: Settings,
        onProgress?: (progress: number) => void
    ): Promise<Blob> {
        if (onProgress) onProgress(20);

        // Create image element to load the file
        const img = new Image();
        const imageLoadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Failed to load image"));
        });
        img.src = URL.createObjectURL(file);
        
        await imageLoadPromise;
        if (onProgress) onProgress(40);

        // Calculate dimensions
        let width = img.width;
        let height = img.height;

        if (settings.maxWidth || settings.maxHeight) {
            const aspectRatio = width / height;
            
            if (settings.maxWidth && settings.maxHeight) {
                // Scale to fit within both dimensions
                if (width > settings.maxWidth || height > settings.maxHeight) {
                    if (width / settings.maxWidth > height / settings.maxHeight) {
                        width = settings.maxWidth;
                        height = Math.round(width / aspectRatio);
                    } else {
                        height = settings.maxHeight;
                        width = Math.round(height * aspectRatio);
                    }
                }
            } else if (settings.maxWidth && width > settings.maxWidth) {
                width = settings.maxWidth;
                height = Math.round(width / aspectRatio);
            } else if (settings.maxHeight && height > settings.maxHeight) {
                height = settings.maxHeight;
                width = Math.round(height * aspectRatio);
            }
        }

        if (onProgress) onProgress(60);

        // Create canvas
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        
        if (!ctx) {
            throw new Error("Failed to get canvas context");
        }

        // Draw image to canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Apply black and white filter if requested
        if (settings.convertToBlackAndWhite) {
            const imageData = ctx.getImageData(0, 0, width, height);
            const data = imageData.data;
            
            for (let i = 0; i < data.length; i += 4) {
                const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
                data[i] = avg;     // Red
                data[i + 1] = avg; // Green
                data[i + 2] = avg; // Blue
                // Alpha (data[i + 3]) remains unchanged
            }
            
            ctx.putImageData(imageData, 0, 0);
        }

        if (onProgress) onProgress(80);

        // Get MIME type for output format
        const mimeType = ImageProcessor.getMimeType(settings.outputFormat);
        
        // Convert canvas to blob with appropriate format and quality
        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error("Failed to convert canvas to blob"));
                    }
                },
                mimeType,
                // Quality applies to JPEG and WebP formats
                (settings.outputFormat === "jpeg" || settings.outputFormat === "webp") 
                    ? settings.quality / 100  // Canvas expects quality as 0-1
                    : undefined
            );
        });

        // Clean up
        URL.revokeObjectURL(img.src);

        if (onProgress) onProgress(100);
        return blob;
    }

    /**
     * Get the MIME type for a given output format
     */
    private static getMimeType(format: string): string {
        const mimeTypes: Record<string, string> = {
            png: "image/png",
            jpeg: "image/jpeg",
            webp: "image/webp",
            bmp: "image/bmp",
            tiff: "image/tiff",
            gif: "image/gif",
        };

        return mimeTypes[format] || "image/png";
    }

    /**
     * Get file extension for a given output format
     */
    static getFileExtension(format: string): string {
        const extensions: Record<string, string> = {
            png: ".png",
            jpeg: ".jpg",
            webp: ".webp",
            bmp: ".bmp",
            tiff: ".tiff",
            gif: ".gif",
        };

        return extensions[format] || ".png";
    }

    /**
     * Generate output filename based on original filename and settings
     */
    static generateOutputFilename(originalFilename: string, settings: Settings): string {
        // Remove the original extension
        const nameWithoutExt = originalFilename.replace(/\.[^/.]+$/, "");

        // Build suffix with processing parameters
        let suffix = "_optimized";

        // Add dimensions if resized
        if (settings.maxWidth || settings.maxHeight) {
            suffix += "_resized";
        }

        // Add quality for JPEG and WebP
        if ((settings.outputFormat === "jpeg" || settings.outputFormat === "webp") && settings.quality) {
            suffix += `_q${settings.quality}`;
        }

        // Add max width if specified
        if (settings.maxWidth) {
            suffix += `_w${settings.maxWidth}`;
        }

        // Add max height if specified
        if (settings.maxHeight) {
            suffix += `_h${settings.maxHeight}`;
        }

        // Add black and white indicator
        if (settings.convertToBlackAndWhite) {
            suffix += "_bw";
        }

        // Add new extension
        const extension = ImageProcessor.getFileExtension(settings.outputFormat);

        return `${nameWithoutExt}${suffix}${extension}`;
    }

    /**
     * Download a blob as a file
     */
    static downloadBlob(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}
